import React, { useState, useEffect } from 'react';
import { Select } from './ui/select';
import { Button } from './ui/button';
import { Label } from './ui/label';
import CardPreview from './CardPreview'; // Import the CardPreview component

interface QRCodeDisplayProps {
  qrCodeData: string | undefined;
  qrCodeVersion: string | undefined; // Add the QR code version prop
  demographics: {
    fullName: string;
    dob: string;
    gender: string;
    address: string;
    photo: string | null; // Base64 photo
  };
}

const QRCodeDisplay: React.FC<QRCodeDisplayProps> = ({ qrCodeData, qrCodeVersion, demographics }) => {
  const [selectedSize, setSelectedSize] = useState<string>('5x5'); // Default to "original"
  const [resizedQrCode, setResizedQrCode] = useState<string | undefined>(qrCodeData); // State for resized QR code

  // Size options in cm and their corresponding pixel dimensions at 500 DPI
  const sizeOptions: Record<string, number> = {
    original: 0, // Original size
    '2x2': 394, // 2x2 cm at 500 DPI
    '3x3': 591, // 3x3 cm at 500 DPI
    '4x4': 787, // 4x4 cm at 500 DPI
    '5x5': 984, // 5x5 cm at 500 DPI
  };

  // Handle size selection
  const handleSizeChange = (event: React.ChangeEvent<HTMLSelectElement>) => {
    const newSize = event.target.value;
    setSelectedSize(newSize);

    if (newSize === 'original') {
      // Reset to the original QR code
      setResizedQrCode(qrCodeData);
    } else {
      // Resize the QR code
      resizeQrCode(newSize);
    }
  };

  // Resize the QR code image
  const resizeQrCode = (sizeKey: string) => {
    if (!qrCodeData || sizeKey === 'original') return;

    const canvas = document.createElement('canvas');
    const context = canvas.getContext('2d');
    const image = new Image();

    image.onload = () => {
      const newSize = sizeOptions[sizeKey];
      canvas.width = newSize;
      canvas.height = newSize;

      if (context) {
        context.drawImage(image, 0, 0, newSize, newSize);

        // Update the resized QR code
        const resizedImage = canvas.toDataURL('image/png');
        setResizedQrCode(resizedImage);
      }
    };

    image.src = qrCodeData;
  };

  // Update the resized QR code when the original QR code changes
  useEffect(() => {
    if (selectedSize === 'original') {
      setResizedQrCode(qrCodeData);
    } else {
      resizeQrCode(selectedSize);
    }
  }, [qrCodeData]);

  return (
    <div className="text-center my-4">
      <h2 className="text-xl font-semibold mb-2">Card Preview</h2>
      <p className="text-sm text-muted-foreground mt-1">
        QR Code Version: {qrCodeVersion || 'N/A'}
      </p>

      {/* Card Preview */}
      <CardPreview
        demographics={demographics}
        qrCodeImage={resizedQrCode}
        selectedSize={selectedSize}
      />

      {/* Dropdown for selecting size */}
      <div className="mt-4">
        <Label htmlFor="qr-size-select" className="block mb-2">
          Select QR Code Size:
        </Label>
        <Select
          id="qr-size-select"
          value={selectedSize}
          onChange={handleSizeChange}
          className="w-[140px] mx-auto"
        >
          <option value="original">Original</option>
          <option value="2x2">2x2</option>
          <option value="3x3">3x3</option>
          <option value="4x4">4x4</option>
          <option value="5x5">5x5</option>
        </Select>
      </div>

      {/* Download QR Code Button */}
      <Button
        onClick={() => {
          if (resizedQrCode) {
            const link = document.createElement('a');
            link.href = resizedQrCode; // Use the resized QR code data URL
            link.download = `qr-code-${selectedSize}.png`; // Set the file name based on the selected size
            link.click();
          }
        }}
        className="mt-4"
        disabled={!resizedQrCode} // Disable the button if no QR code is available
      >
        Download QR Code
      </Button>
    </div>
  );
};

export default QRCodeDisplay;